/**
 * FilterPanel Component
 *
 * Sidebar filters for methods with i18n support
 */

import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import type { Filters, FiltersResponse } from '../types';

interface FilterPanelProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  availableFilters: FiltersResponse['data'] | null;
  loading: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  setFilters,
  availableFilters,
  loading,
}) => {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="filter-panel">
        <div className="flex items-center gap-2">
          <div className="spinner"></div>
          <span className="text-sm text-gray-600">{t('loadingFilters')}</span>
        </div>
      </div>
    );
  }

  const handleModuleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = e.target.value;
    setFilters({
      ...filters,
      module: value ? parseInt(value, 10) : undefined,
    });
  };

  const handleTopicChipToggle = (topicName: string): void => {
    const current = filters.topics ?? [];
    const next = current.includes(topicName)
      ? current.filter(t => t !== topicName)
      : [...current, topicName];
    setFilters({
      ...filters,
      topics: next.length > 0 ? next : undefined,
    });
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = e.target.value;
    setFilters({
      ...filters,
      tag: value ? parseInt(value, 10) : undefined,
    });
  };

  const handleDurationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value ? parseInt(value, 10) : undefined,
    });
  };

  const handleReset = (): void => {
    setFilters({ lang: filters.lang });
  };

  return (
    <div className="filter-panel space-y-6">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <h2 className="text-lg font-bold text-navy">{t('filters')}</h2>
      </div>

      {/* Module filter */}
      {availableFilters && availableFilters.modules && availableFilters.modules.length > 0 && (
        <div>
          <label htmlFor="module-filter" className="filter-label">
            {t('module')}
          </label>
          <select
            id="module-filter"
            aria-label={t('module')}
            value={filters.module || ''}
            onChange={handleModuleChange}
            className="filter-select"
          >
            <option value="">{t('allModules')}</option>
            {availableFilters.modules.map((module) => (
              <option key={module.id} value={module.id}>
                {module.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Topic filter - chip multi-select */}
      {availableFilters && availableFilters.topics && availableFilters.topics.length > 0 && (
        <div>
          <p className="filter-label">{t('topic')}</p>
          <div className="grid grid-cols-2 gap-1.5 mt-1">
            {availableFilters.topics.map((topic) => {
              const isSelected = (filters.topics ?? []).includes(topic.name);
              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => handleTopicChipToggle(topic.name)}
                  className={
                    isSelected
                      ? 'flex items-center justify-center text-center px-2 py-1 rounded-full text-xs font-medium bg-teal text-white border border-teal cursor-pointer transition-colors leading-tight'
                      : 'flex items-center justify-center text-center px-2 py-1 rounded-full text-xs font-medium bg-white text-navy border border-gray-300 cursor-pointer hover:border-teal transition-colors leading-tight'
                  }
                >
                  {topic.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tags filter */}
      {availableFilters && availableFilters.tags && availableFilters.tags.length > 0 && (
        <div>
          <label htmlFor="tag-filter" className="filter-label">
            {t('activityType')}
          </label>
          <select
            id="tag-filter"
            aria-label={t('activityType')}
            value={filters.tag || ''}
            onChange={handleTagChange}
            className="filter-select"
          >
            <option value="">{t('allActivityTypes')}</option>
            {availableFilters.tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Duration filter */}
      {availableFilters && availableFilters.duration_range && (
        <div>
          <label htmlFor="min-duration" className="filter-label">
            {t('durationMinutes')}
          </label>
          <div className="space-y-3">
            <input
              type="number"
              id="min-duration"
              name="min_duration"
              aria-label={t('min')}
              placeholder={t('min')}
              value={filters.min_duration || ''}
              onChange={handleDurationChange}
              min={availableFilters.duration_range.min}
              max={availableFilters.duration_range.max}
              className="filter-input"
            />
            <input
              type="number"
              id="max-duration"
              name="max_duration"
              aria-label={t('max')}
              placeholder={t('max')}
              value={filters.max_duration || ''}
              onChange={handleDurationChange}
              min={availableFilters.duration_range.min}
              max={availableFilters.duration_range.max}
              className="filter-input"
            />
          </div>
        </div>
      )}

      {/* Reset button */}
      <button
        onClick={handleReset}
        className="btn-outline w-full"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        {t('resetFilters')}
      </button>
    </div>
  );
};
